/** The order record's header (plan §1.1 "same for /orders"): the contact's name — or
 *  the order ref when the order names no contact — with the state chip, an Inactive chip
 *  and the restech chip beside it; on the right the Actions menu and Edit (`E`) — or, in
 *  edit mode, Close editor (`Esc`). The subtitle reads tenant · order ref · its bookings
 *  · the start, in UTC and labelled · ID · the machine ID, each with its value actions.
 *
 *  No `console_orders_edit` (or the kill switch off) → no Edit button at all: a disabled
 *  one would only advertise power the operator lacks. The Actions menu holds the order's
 *  immediate actions (`useOrderImmediateActions`), led by the kill switch's note
 *  (`EDITING_OFF`). The right side is the records' `RecordHeaderActions`. */
import { Box, Chip, Skeleton } from '@mui/material';

import type { OrderRecordEditContext } from '@/api/orderRecord';
import type { OrderDetail } from '@/api/orders';
import { PageHeader } from '@/components/PageHeader';
import { act, DetailSubtitle, ValueActions } from '@/components/ValueActions/ValueActions';
import { BookingStateChip } from '@/features/bookings/BookingStateChip';
import { tenantActions } from '@/features/bookings/overview/bookingLinks';
import { MONO_SX } from '@/features/bookings/record/panels/displays';
import { EDITING_OFF } from '@/features/records/header/editingOff';
import { RecordHeaderActions } from '@/features/records/header/RecordHeaderActions';
import type { RecordActionGroup } from '@/features/records/page/RecordActionsMenu';
import { bookingsHref, cond } from '@/utils/gridLinks';
import { formatZoned } from '@/utils/zonedTime';

import {
  orderActiveActions,
  orderCustomIdActions,
  orderIdActions,
  orderRefActions,
  orderStateActions,
  type OrderCtx,
} from '../overview/orderLinks';

import { OrderRestechChip } from './RestechChip';
import { ORDER_TIMEZONE } from './views/spineModel';

export interface OrderRecordHeaderProps {
  id: number;
  detail: OrderDetail | undefined;
  editContext: OrderRecordEditContext | undefined;
  /** The active page tab's label (document title). */
  section: string;
  canEdit: boolean;
  editing: boolean;
  /** The kill switch is off: the Actions menu says so. */
  editingDisabled: boolean;
  onEdit: () => void;
  onClose: () => void;
  integrationHref: string;
  actionGroups?: RecordActionGroup[];
}

/** The page title: who placed the order, else its ref, else its id. */
export function orderTitleText(id: number, o: OrderDetail | undefined): string {
  const c = o?.contact;
  const name = [c?.first_name, c?.last_name].filter(Boolean).join(' ');
  return name || o?.order_ref || o?.custom_id || `Order #${id}`;
}

export function OrderRecordHeader({
  id,
  detail: o,
  editContext: ctx,
  section,
  canEdit,
  editing,
  editingDisabled,
  onEdit,
  onClose,
  integrationHref,
  actionGroups = [],
}: OrderRecordHeaderProps) {
  const links: OrderCtx = { tenantId: o?.tenant?.id ?? ctx?.tenant.id };
  const titleText = orderTitleText(id, o);
  const titleIsRef = Boolean(o?.order_ref) && titleText === o?.order_ref;
  const title =
    titleIsRef && o?.order_ref ? <ValueActions actions={orderRefActions(o.order_ref, links)}>{titleText}</ValueActions> : titleText;

  const count = o?.booking_count ?? 0;
  const bookingsLabel = `${count} ${count === 1 ? 'booking' : 'bookings'}`;
  const subtitle = o ? (
    <DetailSubtitle
      nowrap
      parts={[
        o.tenant && <ValueActions actions={tenantActions(o.tenant)}>{o.tenant.name}</ValueActions>,
        o.order_ref && !titleIsRef && (
          <ValueActions actions={orderRefActions(o.order_ref, links)}>
            Order{' '}
            <Box component="span" sx={{ ...MONO_SX, color: 'text.primary' }}>
              {o.order_ref}
            </Box>
          </ValueActions>
        ),
        o.order_ref ? (
          <ValueActions actions={[act.filter(bookingsHref([cond('order', o.order_ref)], links.tenantId ?? null), 'Bookings in this order')]}>
            {bookingsLabel}
          </ValueActions>
        ) : (
          bookingsLabel
        ),
        o.start_datetime && formatZoned(o.start_datetime, ORDER_TIMEZONE, { year: 'auto' }),
        <ValueActions key="id" actions={orderIdActions(id)}>
          ID {id}
        </ValueActions>,
        o.custom_id && (
          <ValueActions actions={orderCustomIdActions(o.custom_id, links)}>
            Machine ID{' '}
            <Box component="span" sx={{ ...MONO_SX, color: 'text.primary' }}>
              {o.custom_id}
            </Box>
          </ValueActions>
        ),
      ]}
    />
  ) : (
    <Skeleton variant="text" width={420} />
  );

  const chips = o ? (
    <>
      <ValueActions chip actions={orderStateActions(o.booking_state_display, links)}>
        <BookingStateChip booking_state={o.booking_state} booking_state_display={o.booking_state_display} />
      </ValueActions>
      {!o.active && (
        <ValueActions chip actions={orderActiveActions(false, links)}>
          <Chip size="small" variant="outlined" label="Inactive" />
        </ValueActions>
      )}
      {ctx && <OrderRestechChip restech={ctx.restech} remoteUpdatedTs={o.remote_updated_ts} integrationHref={integrationHref} />}
    </>
  ) : undefined;

  const actions = (
    <RecordHeaderActions
      groups={actionGroups}
      note={editingDisabled ? EDITING_OFF : null}
      canEdit={canEdit}
      editing={editing}
      onEdit={onEdit}
      onClose={onClose}
    />
  );

  return (
    <PageHeader
      helpStory="order-detail"
      section={section}
      title={title}
      titleText={titleText}
      titleAdornment={chips}
      subtitle={subtitle}
      actions={actions}
    />
  );
}
